import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, CheckCircle2 } from 'lucide-react';
import emailjs from '@emailjs/browser';

// Initialize EmailJS with your public key
emailjs.init('vitthDXmLUdJTTIie');

export default function ContactModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    let digits = value.replace(/\D/g, '');

    // If it starts with 1 (from +1 prefix), remove it to avoid duplication
    if (digits.startsWith('1')) {
      digits = digits.slice(1);
    }

    // Limit to 10 digits
    digits = digits.slice(0, 10);

    // Format as +1 (XXX) XXX-XXXX
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `+1 (${digits}`;
    if (digits.length <= 6) return `+1 (${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    // Format phone number as user types
    if (name === 'phone') {
      formattedValue = formatPhoneNumber(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // Send email using EmailJS
      await emailjs.send(
        'service_hvkwf45',  // Service ID
        'template_qmklt89', // Template ID
        {
          from_name: `${formData.firstName} ${formData.lastName}`,
          from_email: formData.email,
          phone: formData.phone || 'Not provided',
          message: formData.message,
          to_email: 'info@primeslink.com'
        }
      );

      setIsSuccess(true);
      setFormData({ firstName: '', lastName: '', email: '', phone: '', message: '' });
      setTimeout(() => { onClose(); setIsSuccess(false); }, 3000);
    } catch (err) {
      console.error('Email error:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" />

          <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }} transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 pointer-events-none">
            <div className="bg-dark-950 border border-white/20 rounded-2xl shadow-2xl max-w-lg w-full my-8 max-h-[90vh] flex flex-col pointer-events-auto">
              <div className="flex justify-between items-center p-6 sm:p-8 border-b border-white/10 bg-dark-900/95 backdrop-blur rounded-t-2xl">
                <h2 className="text-2xl sm:text-3xl font-bold text-white">Get in Touch</h2>
                <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}
                  onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                  <X size={24} className="text-gray-400" />
                </motion.button>
              </div>

              <div className="overflow-y-auto flex-1 p-6 sm:p-8">
                {isSuccess ? (
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    className="flex flex-col items-center justify-center py-12 text-center">
                    <CheckCircle2 size={64} className="text-primary-500 mb-4" />
                    <h3 className="text-2xl font-bold text-white mb-2">Message Sent!</h3>
                    <p className="text-gray-400">Thank you for reaching out. We'll get back to you soon.</p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">First Name *</label>
                        <input type="text" name="firstName" value={formData.firstName} onChange={handleChange}
                          required placeholder="John" className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 focus:bg-white/15 transition-all" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Last Name *</label>
                        <input type="text" name="lastName" value={formData.lastName} onChange={handleChange}
                          required placeholder="Doe" className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 focus:bg-white/15 transition-all" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Email *</label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange}
                        required placeholder="john@example.com" className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 focus:bg-white/15 transition-all" />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Phone Number *</label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                        required placeholder="+1 (555) 123-4567" className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 focus:bg-white/15 transition-all" />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Message *</label>
                      <textarea name="message" value={formData.message} onChange={handleChange}
                        required placeholder="Tell us about your project or ask any questions..." rows="4"
                        className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 focus:bg-white/15 transition-all resize-none" />
                    </div>

                    {error && (
                      <div className="p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-300 text-sm">{error}</div>
                    )}

                    <motion.button type="submit" disabled={isSubmitting}
                      whileHover={{ scale: isSubmitting ? 1 : 1.02 }} whileTap={{ scale: 0.98 }}
                      className="w-full py-3 bg-gradient-to-r from-primary-500 to-accent-500 text-white rounded-lg font-bold flex items-center justify-center gap-2 hover:shadow-2xl hover:shadow-accent-500/50 transition-shadow disabled:opacity-50 disabled:cursor-not-allowed">
                      <Send size={18} />
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </motion.button>

                    <p className="text-xs text-gray-500 text-center">* Required fields</p>
                  </form>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
